import Raven from 'raven-js';
import enviro from 'enviro';
import { RecaptchaIntegration } from '../types/BasePaymentSessionsResponse';

const TEST_SITE_KEY = '6Le6rD8kAAAAAFAKCOH96PLU8P5WE1RWz5Sz5Quh';
const PROD_SITE_KEY = '6LdZL7wkAAAAAJ8fv2_ZtTKDmr22AeNATST8naKi';

const GOOGLE_RECAPTCHA_SCRIPT_URL =
  'https://www.google.com/recaptcha/enterprise.js';
const FALLBACK_SCRIPT_URL = 'https://recaptcha.net/recaptcha/enterprise.js';

type GlobalThis = typeof globalThis &
  Window & {
    NaN: never;
    Infinity: never;
  };
interface GoogleReCaptchaWindow extends GlobalThis {
  grecaptcha: {
    enterprise: {
      render: any;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
      ready: (callback: () => void) => void;
    };
  };
  [key: string]: any;
}

export function addRecaptchaScript(isATPortal: boolean) {
  const isQA = enviro.isQa();
  const siteKey = isQA || isATPortal ? TEST_SITE_KEY : PROD_SITE_KEY;

  const script = createRecaptchaScript({
    src: GOOGLE_RECAPTCHA_SCRIPT_URL,
    siteKey,
    onError: () => {
      createRecaptchaScript({
        src: FALLBACK_SCRIPT_URL,
        siteKey,
        onError: (e: string | Event) => {
          throw e;
        },
      });
    },
  });
  document.head.appendChild(script);
}

function createRecaptchaScript({
  src,
  siteKey,
  onError,
}: {
  src?: string;
  siteKey: string;
  onError: (e: string | Event) => void;
}) {
  const script = document.createElement('script');
  script.async = true;
  script.type = 'text/javascript';
  script.src = `${src}?render=${siteKey}`;
  script.onerror = onError;
  return script;
}

export const getRecaptchaToken = (
  isATPortal: boolean,
  action: string
): Promise<string | null> => {
  const isQA = enviro.isQa();
  const siteKey = isQA || isATPortal ? TEST_SITE_KEY : PROD_SITE_KEY;

  if (!(window as GoogleReCaptchaWindow).grecaptcha) {
    Raven.captureMessage(
      `window.grecaptcha failed to load, submitting payment with null token`
    );
    return Promise.resolve(null);
  }

  return new Promise<string>((resolve, reject) => {
    const rejectWithReport = (err: unknown) => {
      Raven.captureMessage(
        `The grecaptcha.enterprise.execute failed with error: ${err}`
      );
      reject(err);
    };
    (window as GoogleReCaptchaWindow).grecaptcha.enterprise.ready(() => {
      try {
        (window as GoogleReCaptchaWindow).grecaptcha.enterprise
          .execute(siteKey, {
            action,
          })
          .then(resolve)
          .catch(rejectWithReport);
      } catch (err) {
        rejectWithReport(err);
      }
    });
  });
};

export const getIsRecaptchaEnabled = ({
  liveMode,
  recaptchaIntegration,
}: {
  liveMode?: boolean;
  recaptchaIntegration?: RecaptchaIntegration;
}) => Boolean(liveMode && recaptchaIntegration?.isEnabled);
